import React from 'react';
import { formatCurrency } from '@/utils/formatters';
import SummaryLineItem from './SummaryLineItem';
import { calculatePaymentTotals, getAmountDueToday } from '@/utils/payment/paymentCalculations';
import { getNextFirstOfMonthLabel } from '@/utils/anchorBilling';

interface PaymentSummaryProps {
  product: any;
  additionalProducts?: any[];
  selectedPaymentMethod?: 'bank' | 'card' | null;
  grossCalculation?: any; // Keep for backward compatibility but use new calculation
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  product,
  additionalProducts = [],
  selectedPaymentMethod,
}) => {
  const allProducts = [product, ...additionalProducts];
  
  const { total, isProrated, daysRemaining } = calculatePaymentTotals(
    allProducts,
    selectedPaymentMethod
  );

  const hasNonAnchorItems = allProducts.some((item) => {
    const isSub =
      item.is_subscription ||
      item.isSubscription ||
      (item.billing_period && item.billing_period !== 'one_time');
    const usesAnchor = item.use_anchor_billing ?? item.useAnchorBilling;
    return !(isSub && usesAnchor);
  });

  const isAnchorSubscription = (item: any) => {
    const isSub =
      item.is_subscription ||
      item.isSubscription ||
      (item.billing_period && item.billing_period !== 'one_time');
    const usesAnchor = item.use_anchor_billing ?? item.useAnchorBilling;
    return Boolean(isSub && usesAnchor);
  };
  
  // Helper to get billing period suffix
  const getPeriodSuffix = (item: any) => {
    if (!item.billing_period) return '';
    switch (item.billing_period) {
      case 'monthly': return '/month';
      case 'yearly': return '/year';
      case 'weekly': return '/week';
      case 'daily': return '/day';
      case 'quarterly': return '/quarter';
      default: return '';
    }
  };

  return (
    <div className="bg-white border rounded-md p-4">
      <h3 className="font-semibold text-2xl pb-2 mb-2">Order Summary</h3>
      <div className="space-y-2 mb-4">
        {allProducts.map((item, index) => {
          const dueToday = getAmountDueToday(item);
          const periodSuffix = getPeriodSuffix(item);
          const isProratedItem = isAnchorSubscription(item);

          return (
            <SummaryLineItem
              key={index}
              label={item.title || item.name || 'Product'}
              amount={dueToday}
              description={
                isProratedItem
                  ? `then ${formatCurrency(item.amount || 0)}${periodSuffix}`
                  : (item.description || '')
              }
              periodSuffix={isProratedItem ? '' : periodSuffix}
            />
          );
        })}
      </div>
      {isProrated && (
        <p className="text-xs text-muted-foreground mb-2">
          {hasNonAnchorItems
            ? `Subscription items are prorated for ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} until ${getNextFirstOfMonthLabel()}. One-time items are charged in full.`
            : `Prorated for ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} until ${getNextFirstOfMonthLabel()}. Full amount applies on the next billing cycle.`}
        </p>
      )}
      <div className="pt-2 space-y-2">
        <div className="flex justify-between font-semibold border-t pt-2">
          <span>{isProrated ? 'Total Due Today:' : 'Total Due:'}</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
